import { CartAddEvent } from '@theme/events';
import { bundleStore } from '@theme/bundle-builder-store';
import {
  getTotalPrice,
  getProduct,
  sellingPlanIdsMatch,
} from '@theme/bundle-builder-store-getters';
import { getActiveThreshold } from '@theme/bundle-builder-tier-config';
import {
  trackBybProductViewed,
  trackBybProductAdded,
  trackAddToCart,
  trackViewItem,
  setGtmCartAttributesBatch,
  setListContext,
  normalizeShopifyResourceId,
} from '@theme/gtm-events';

/**
 * BYB-specific GTM events.
 *
 * view_item          — rolled-up shell product (same shape as Starter Box PDP).
 * byb_product_viewed — fires on BYB page load for all products in the grid.
 * byb_product_added  — fires when the BYB "Add to Cart" / "Update Bundle" button
 *                      succeeds (bundle-builder-add-to-cart | bundle-builder-update-bundle).
 * add_to_cart        — rolled-up box ecommerce event for new box adds only
 *                      (bundle-builder-add-to-cart). Edit/replace must not fire ATC:
 *                      the cart swap zeroes old lines without remove_from_cart.
 */

const BYB_EVENT_SOURCES = new Set([
  'bundle-builder-add-to-cart',
  'bundle-builder-update-bundle',
]);

/** New box add only — do not include edit/replace (`bundle-builder-update-bundle`). */
const BYB_ATC_SOURCE = 'bundle-builder-add-to-cart';

/**
 * @param {unknown} badge
 * @returns {string}
 */
function normalizeProductBadge(badge) {
  if (badge == null || badge === '') return '';
  if (Array.isArray(badge)) return normalizeProductBadge(badge[0]);
  if (typeof badge === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (badge);
    return String(obj.label ?? obj.name ?? obj.text ?? obj.value ?? '');
  }
  return String(badge);
}

/**
 * @returns {{
 *   bundleProducts: any[],
 *   bundleCollections: any[],
 *   bundleProduct: { id?: string | number, title?: string } | null,
 *   listId: string,
 *   listName: string,
 * } | null}
 */
function readBybDataLayer() {
  const el = document.querySelector('bundle-builder-data-layer-component');
  if (!el) return null;

  try {
    const bundleProducts = JSON.parse(/** @type {HTMLElement} */ (el).dataset.bundleProducts ?? '[]');
    const bundleCollections = JSON.parse(
      /** @type {HTMLElement} */ (el).dataset.bundleCollections ?? '[]'
    );
    const bundleProduct = JSON.parse(/** @type {HTMLElement} */ (el).dataset.bundleProduct ?? 'null');
    const listId = bundleCollections?.[0]?.handle ?? 'build_your_box';
    const listName = bundleCollections?.[0]?.title ?? 'Build Your Box';
    return { bundleProducts, bundleCollections, bundleProduct, listId, listName };
  } catch (_) {
    return null;
  }
}

/**
 * 1-based index of a product in the on-page BYB grid (same order as byb_product_viewed).
 * @param {any[]} bundleProducts
 * @param {string | number} productId
 * @returns {number | null}
 */
function gridIndexForProduct(bundleProducts, productId) {
  const idx = bundleProducts.findIndex((p) => String(p.id) === String(productId));
  return idx >= 0 ? idx + 1 : null;
}

// ---------------------------------------------------------------------------
// view_item (rolled-up shell) + byb_product_viewed — from data-layer element
// ---------------------------------------------------------------------------

function fireBybLandingEvents() {
  const data = readBybDataLayer();
  if (!data?.bundleProducts?.length) return;

  const { bundleProducts, bundleProduct, listId, listName } = data;
  const boxTier = getActiveThreshold();

  // Rolled-up view_item for the shell (matches Starter Box view_item shape).
  // Landing only — skip subscription editor and cart edit (`?edit=`). Edit
  // hydration is async, so firing here would seed list context and emit the
  // default tier before the box being edited is loaded.
  const params = new URLSearchParams(window.location.search);
  const isLandingViewItem =
    !params.has('subscription_id') &&
    !params.has('edit') &&
    !document.querySelector('bundle-builder-subscription-loader-component');

  if (isLandingViewItem && bundleProduct?.id) {
    setListContext(String(bundleProduct.id), listId, listName, 1);
    trackViewItem({
      item_id: String(bundleProduct.id),
      item_name: bundleProduct.title ?? 'Build Your Box',
      item_category: '',
      price: 0,
      box_type: 'Build Your Box',
      box_size: boxTier,
      box_capacity: boxTier,
      item_list_id: listId,
      item_list_name: listName,
      index: 1,
    });
  }

  // Do not setListContext for grid products — that overwrites collection /
  // select_item browse attribution in sessionStorage. Shell seeding above is
  // enough; byb_product_added resolves index via gridIndexForProduct.
  const items = bundleProducts.map((product, i) => ({
    index: i + 1,
    item_id: String(product.id),
    item_name: product.title,
    item_brand: product.vendor ?? '',
    item_category: product.type ?? '',
    price: product.variants?.[0]?.price ?? 0,
    quantity: 0,
    variant_item_id: product.variants?.[0]?.sku ?? '',
    product_badge: normalizeProductBadge(product.productBadge),
    box_type: 'Build Your Box',
    box_size: boxTier,
    box_capacity: boxTier,
  }));

  trackBybProductViewed(listId, listName, items);
}

// Reads product list from the Liquid data-layer element (not bundleStore).
// DOMContentLoaded is enough — the element is in the initial HTML.
document.addEventListener('DOMContentLoaded', fireBybLandingEvents, { once: true });

// ---------------------------------------------------------------------------
// byb_product_added (+ rolled-up add_to_cart on new add only) — after BYB cart write
// ---------------------------------------------------------------------------

document.addEventListener(CartAddEvent.eventName, async (e) => {
  const source = /** @type {CartAddEvent} */ (e).detail?.data?.source ?? '';
  if (!BYB_EVENT_SOURCES.has(source)) return;

  const state = bundleStore.getState();
  if (!state?.items?.length) return;

  const data = readBybDataLayer();
  const bundleProducts = data?.bundleProducts ?? [];
  /** @type {Record<string, { vendor: string, type: string, productBadge: unknown }>} */
  const bundleProductsMap = {};
  for (const p of bundleProducts) {
    bundleProductsMap[String(p.id)] = {
      vendor: p.vendor ?? '',
      type: p.type ?? '',
      productBadge: p.productBadge,
    };
  }

  const listId = data?.listId ?? state.collections?.[0]?.handle ?? 'build_your_box';
  const listName = data?.listName ?? state.collections?.[0]?.title ?? 'Build Your Box';

  const selectedItems = state.items
    .filter((item) => (item.quantity ?? 0) > 0)
    .map((item) => {
      const product = getProduct(item.productId);
      const productMeta = bundleProductsMap[String(item.productId)];
      // sku lives on Variant, not BundleItem — look it up via variantId
      const sku = product?.variants.find((v) => v.id === item.variantId)?.sku ?? '';
      const gridIndex = gridIndexForProduct(bundleProducts, item.productId);
      return {
        // Prefer on-page grid position (byb_product_viewed), not selection order
        index: gridIndex ?? 1,
        item_id: String(item.productId),
        item_name: product?.title ?? '',
        item_brand: productMeta?.vendor ?? '',
        item_category: productMeta?.type ?? '',
        price: item.price ?? 0,
        quantity: item.quantity,
        variant_item_id: sku,
        product_badge: normalizeProductBadge(productMeta?.productBadge),
      };
    });

  if (!selectedItems.length) return;

  const totalCents = getTotalPrice();
  const boxTier = getActiveThreshold();
  const purchaseType = state.purchaseType === 'subscribe' ? 'subscription' : 'one_time';

  // Resolve the human-readable plan name (e.g. "Every 2 Weeks") from sellingPlanGroups.
  const sellingPlanFrequency = (() => {
    if (!state.sellingPlanId) return '';
    for (const group of state.sellingPlanGroups ?? []) {
      for (const plan of group?.selling_plans ?? []) {
        if (sellingPlanIdsMatch(plan?.id, state.sellingPlanId)) return plan?.name ?? '';
      }
    }
    return '';
  })();

  trackBybProductAdded(
    {
      value: totalCents,
      listId,
      listName,
      deliveryFrequency: sellingPlanFrequency,
      boxType: 'Build Your Box',
      boxSize: boxTier,
      boxCapacity: boxTier,
      purchaseType,
    },
    selectedItems
  );

  const boxProduct = state.bundleProduct;
  const boxProductId = normalizeShopifyResourceId(boxProduct?.id);

  // Persist rolled-up box attrs for checkout Custom Pixel (+ per-component list ids)
  // in one cart/update so concurrent writes cannot race.
  const attrEntries = [];
  if (boxProductId) {
    attrEntries.push({
      productId: boxProductId,
      data: {
        box_type: 'Build Your Box',
        box_size: boxTier,
        box_capacity: boxTier,
        item_list_id: listId,
        item_list_name: listName,
        index: 1,
      },
    });
  }
  for (const item of selectedItems) {
    const componentId = normalizeShopifyResourceId(item.item_id);
    if (!componentId) continue;
    attrEntries.push({
      productId: componentId,
      data: {
        box_type: 'Build Your Box',
        box_size: boxTier,
        box_capacity: boxTier,
        item_list_id: listId,
        item_list_name: listName,
        index: item.index,
      },
    });
  }
  if (attrEntries.length) setGtmCartAttributesBatch(attrEntries);

  // Rolled-up GA4 add_to_cart for new box adds only (not cart edit/replace).
  if (source !== BYB_ATC_SOURCE) return;

  let cartSubtotal = totalCents;
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    cartSubtotal = cart.items_subtotal_price ?? cart.total_price ?? totalCents;
  } catch (_) {}

  trackAddToCart(
    {
      item_id: boxProductId || 'build_your_box',
      item_name: boxProduct?.title ?? 'Build Your Box',
      item_category: '',
      price: totalCents,
      quantity: 1,
      index: 1,
      box_type: 'Build Your Box',
      box_size: boxTier,
      box_capacity: boxTier,
      purchase_type: purchaseType,
      delivery_frequency: sellingPlanFrequency,
      item_list_id: listId,
      item_list_name: listName,
    },
    cartSubtotal
  );
});
